import React from "react";
import { createPortal } from "react-dom";
import BreadCrumb from "./BreadCrumb";
import { FaCheck, FaFilter, FaSearch, FaTimes } from "react-icons/fa";
import { LuChevronDown } from "react-icons/lu";
import { useAuth } from "../authContext";
import { toast } from "react-toastify";
import { formatTierName, formatMediaType, formatAudience, formatFieldValue } from "../utils/formatters";

// const getFilterOptions = (category) => {
//   const commonFilters = {
//     budget: ['All', 'Under ₹5000', '₹5000-₹15000', '₹15000-₹50000', 'Above ₹50000'],
//     location: ['All', 'Pan India', 'Regional', 'Local'],
//     duration: ['All', '1-7 days', '7-15 days', '15-30 days', '30+ days']
//   };

//   switch (category) {
//     case 'influencer':
//       return {
//         ...commonFilters,
//         platform: ['All', 'Instagram', 'YouTube', 'Facebook', 'Twitter', 'LinkedIn'],
//         followerRange: ['All', '1K-10K', '10K-100K', '100K-1M', '1M+'],
//         category: ['All', 'Lifestyle', 'Fashion', 'Tech', 'Food', 'Travel', 'Beauty', 'Fitness']
//       };
//     case 'brandEndorsement':
//       return {
//         ...commonFilters,
//         celebrityType: ['All', 'Bollywood', 'Sports', 'Television', 'Regional', 'Digital'],
//         endorsementType: ['All', 'Brand Ambassador', 'One-time Campaign', 'Event Appearance', 'Social Media'],
//         industry: ['All', 'Fashion', 'Technology', 'FMCG', 'Automotive', 'Entertainment']
//       };
//     case 'advertising':
//       return {
//         ...commonFilters,
//         adType: ['All', 'Digital', 'Print', 'TV', 'Radio', 'OOH'],
//         targetAudience: ['All', 'Youth', 'Professionals', 'Families', 'Senior Citizens'],
//         industry: ['All', 'Retail', 'E-commerce', 'Education', 'Healthcare', 'Real Estate']
//       };
//     case 'designing':
//       return {
//         ...commonFilters,
//         designType: ['All', 'Logo', 'Branding', 'Social Media', 'Print', 'Packaging'],
//         complexity: ['All', 'Basic', 'Standard', 'Premium', 'Custom'],
//         deliveryTime: ['All', '24 Hours', '3 Days', '7 Days', 'Custom']
//       };
//     default:
//       return commonFilters;
//   }
// };

const AdvertisingHeader = ({
  category,
  title,
  sortOptions,
  sortBy,
  handleSortClick,
  showSortDropdown,
  setShowSortDropdown,
  onFilterChange,
  filterOptions = {},
  showFilterOptions = true,
  selectedFilters = {},
  showSearch = false,
  searchValue = "",
  onSearchChange,
  searchPlaceholder = "Search"
}) => {
  const { isLoggedIn } = useAuth();
  const [activeFilter, setActiveFilter] = React.useState(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [draftFilters, setDraftFilters] = React.useState({});

  const allFilterKeys = React.useMemo(() => Object.keys(filterOptions), [filterOptions]);
  const quickFilterKeys = React.useMemo(() => allFilterKeys.slice(0, 3), [allFilterKeys]);

  const totalActiveCount = React.useMemo(() => {
    let count = 0;
    Object.entries(selectedFilters).forEach(([_, val]) => {
      if (Array.isArray(val)) {
        count += val.filter((v) => v && v !== "All").length;
      } else if (val && val !== "All") {
        count += 1;
      }
    });
    return count;
  }, [selectedFilters]);

  const draftActiveCount = React.useMemo(() => {
    let count = 0;
    Object.entries(draftFilters).forEach(([_, val]) => {
      if (Array.isArray(val)) {
        count += val.filter((v) => v && v !== "All").length;
      } else if (val && val !== "All") {
        count += 1;
      }
    });
    return count;
  }, [draftFilters]);

  const handleOpenDrawer = () => {
    const initialDraft = {};
    Object.entries(selectedFilters || {}).forEach(([k, v]) => {
      if (Array.isArray(v)) {
        initialDraft[k] = [...v];
      } else if (v && v !== "All") {
        initialDraft[k] = [v];
      }
    });
    setDraftFilters(initialDraft);
    setDrawerOpen(true);
  };

  const handleDraftFilterClick = (filterType, value) => {
    setDraftFilters((prev) => {
      const current = prev[filterType] || [];
      let newValues;

      if (current.includes(value)) {
        newValues = current.filter((v) => v !== value);
      } else {
        newValues = [...current, value];
      }

      return {
        ...prev,
        [filterType]: newValues,
      };
    });
  };

  const handleClearAllDrafts = () => {
    setDraftFilters({});
  };

  const handleApplyDraftFilters = () => {
    Object.entries(draftFilters).forEach(([key, value]) => {
      onFilterChange?.(key, value?.length === 0 ? "All" : value);
    });
    Object.keys(selectedFilters || {}).forEach((key) => {
      if (!(key in draftFilters) || draftFilters[key]?.length === 0) {
        onFilterChange?.(key, "All");
      }
    });
    setDrawerOpen(false);
  };

  const typeFilterLabels = {
    print: "Print Design",
    packing: "Packaging Design",
    digital: "Digital Graphics",
    branding: "Branding Design",
    flex: "Flex & Vinyl",
    paper: "Paper & Sticker",
    marketing: "Marketing & Promotion",
    stationary: "Office Stationary",
  };

  // Format filter values for display
  const formatFilterValue = (filterType, value) => {
    if (!value || value === "All") return value;

    switch (filterType) {
      case "type":
        return typeFilterLabels[value] || formatFieldValue(value);
      case "tier":
        return formatTierName(value);
      case "mediaType":
      case "vehicle_type":
        return formatMediaType(value);
      case "audience":
        return formatAudience(value);
      default:
        return formatFieldValue(value);
    }
  };

  const handleFilterClick = (filterType, value) => {
    const current = selectedFilters[filterType];
    let newValues;

    if (Array.isArray(current)) {
      if (current.includes(value)) {
        newValues = current.filter((v) => v !== value);
      } else {
        newValues = [...current, value];
      }
    } else {
      // Multi-select dropdowns always work with arrays
      newValues = current === value ? [] : [value];
    }

    onFilterChange?.(filterType, newValues);
    // Don't close the dropdown to allow multiple selections
  };

  // Clear an active filter chip (handles both scalar type filters and multi-select arrays)
  const handleRemoveFilter = (filterType, value) => {
    const current = selectedFilters[filterType];

    if (Array.isArray(current)) {
      const next = current.filter((v) => v !== value);
      onFilterChange?.(filterType, next.length > 0 ? next : "All");
      return;
    }

    // Scalar filters (e.g. type from category buttons) clear with "All"
    onFilterChange?.(filterType, "All");
  };

  const handleSortButtonClick = () => {
    if (!isLoggedIn) {
      toast.error("Please login to use sorting features");
      return;
    }
    setShowSortDropdown(!showSortDropdown);
  };

  const handleSortOptionClick = (value) => {
    if (!isLoggedIn) {
      toast.error("Please login to use sorting features");
      return;
    }
    handleSortClick(value);
  };

  const handleSearchInputChange = (event) => {
    onSearchChange?.(event.target.value);
  };

  // Lock body scrolling when drawer is open
  React.useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  // Close dropdowns when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.filter-container')) {
        setActiveFilter(null);
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setShowSortDropdown]);

  const getCategoryTitle = () => {
    if (title) return title;
    switch (category) {
      case "influencer-marketing":
        return "Influencer Marketing";
      case "brand-endorsement":
        return "Brand Endorsement";
      case "designing":
        return "Designing";
      case "printing":
        return "Printing";
      default:
        return category.charAt(0).toUpperCase() + category.slice(1) + " Advertising";
    }
  };

  return (
    <div className="relative z-30 text-white pt-10 pb-4">
      <div className="container relative z-30 mx-auto px-4">
        <BreadCrumb currentPage={getCategoryTitle()} />
        <div className="relative z-30 flex flex-col gap-4">
          <div className="relative z-30 flex flex-col gap-3 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(280px,420px)_auto] lg:items-center">
            <h1 className="text-2xl font-bold text-black sm:text-3xl">
              {getCategoryTitle()}
            </h1>
            {showSearch && (
              <label className="relative z-40 block w-full">
                <span className="sr-only">{searchPlaceholder}</span>
                <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-gray-400" />
                <input
                  type="search"
                  value={searchValue}
                  onChange={handleSearchInputChange}
                  placeholder={searchPlaceholder}
                  className="w-full rounded-xl border border-gray-200 bg-white py-2 pl-11 pr-4 text-sm text-gray-700 shadow-sm outline-none transition focus:border-red-500 focus:ring-2 focus:ring-red-100 sm:text-base"
                />
              </label>
            )}
            <div className="relative z-40 w-full filter-container sm:w-auto">
              <button
                onClick={handleSortButtonClick}
                className={`flex w-full items-center justify-between space-x-2 rounded-lg border px-3 py-2.5 transition-all focus:outline-none sm:w-auto ${
                  isLoggedIn ? 'border-black bg-white text-black hover:bg-gray-50 focus:border-black/40' : 'border-gray-400 bg-gray-100 text-gray-500 cursor-not-allowed'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <span className="text-sm">
                    {sortOptions.find((opt) => opt.value === sortBy)?.label}
                  </span>
                </div>
                <span className={`flex size-5 shrink-0 items-center justify-center rounded-full bg-black text-white transition-transform ${showSortDropdown ? "rotate-180" : ""}`}>
                  <LuChevronDown className="text-xs" />
                </span>
              </button>

              {showSortDropdown && (
                <div className="absolute right-0 z-50 mt-1 max-h-60 w-full min-w-[14rem] overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg sm:w-56">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleSortOptionClick(option.value)}
                      className="group flex w-full items-center justify-between px-3 py-2 text-left text-sm text-black transition-colors hover:bg-[#bc1535] hover:!text-white"
                    >
                      <span>
                        {option.label}
                      </span>
                      {sortBy === option.value && (
                        <FaCheck className="text-sm" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Filters Row */}
          {showFilterOptions && Object.keys(filterOptions).length > 0 && (
            <div className="relative z-30 flex gap-2.5 items-center flex-wrap">
              {/* Quick Filters (Top 3) */}
              {quickFilterKeys.map((filterType) => {
                const options = filterOptions[filterType];
                const activeCount = Array.isArray(selectedFilters[filterType])
                  ? selectedFilters[filterType].length
                  : selectedFilters[filterType] && selectedFilters[filterType] !== "All"
                  ? 1
                  : 0;

                return (
                  <div key={filterType} className="relative z-40 filter-container">
                    <button
                      onClick={() => setActiveFilter(activeFilter === filterType ? null : filterType)}
                      className={`flex w-full items-center justify-between space-x-2 rounded-lg border px-3 py-2 text-xs font-semibold transition-all focus:outline-none sm:w-auto ${
                        activeCount > 0
                          ? "border-red-600 bg-red-50 text-red-700"
                          : "border-gray-300 bg-white text-gray-800 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <FaFilter className={`text-xs ${activeCount > 0 ? "text-red-600" : "text-gray-400"}`} />
                        <span className="capitalize">
                          {filterType} {activeCount > 0 ? `(${activeCount})` : ''}
                        </span>
                      </div>
                      <span className={`flex size-4 shrink-0 items-center justify-center rounded-full text-xs transition-transform ${activeFilter === filterType ? "rotate-180" : ""}`}>
                        <LuChevronDown className="text-xs" />
                      </span>
                    </button>

                    {activeFilter === filterType && (
                      <div className="absolute left-0 z-50 mt-1 max-h-60 w-52 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                        {options.map((option) => (
                          <button
                            key={option}
                            onClick={() => handleFilterClick(filterType, option)}
                            className="group flex w-full items-center justify-between px-3 py-2 text-left text-xs text-black transition-colors hover:bg-[#bc1535] hover:!text-white"
                          >
                            <span>
                              {formatFilterValue(filterType, option)}
                            </span>
                            {selectedFilters[filterType]?.includes(option) && (
                              <FaCheck className="text-xs text-red-600 group-hover:text-white" />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* All Filters Button */}
              <button
                type="button"
                onClick={handleOpenDrawer}
                className="flex items-center gap-2 rounded-lg border border-black bg-black px-3.5 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-neutral-800 focus:outline-none"
              >
                <FaFilter className="text-xs text-red-400" />
                <span>All Filters</span>
                {totalActiveCount > 0 && (
                  <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-[#bc1535] px-1 text-[9px] font-bold text-white">
                    {totalActiveCount}
                  </span>
                )}
              </button>
            </div>
          )}

          {/* Slide-out All Filters Drawer / Modal */}
          {drawerOpen && createPortal(
            <div className="fixed inset-0 z-[99999] flex justify-end">
              {/* Backdrop */}
              <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
                onClick={() => setDrawerOpen(false)}
              />

              {/* Drawer Content */}
              <div className="relative z-[100000] flex h-full w-full max-w-md flex-col bg-white text-gray-900 shadow-2xl">
                {/* Drawer Header */}
                <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-[#bc1535]">
                      <FaFilter className="text-sm" />
                    </div>
                    <div>
                      <h2 className="text-base font-bold text-gray-900">Filter Options</h2>
                      <p className="text-xs text-gray-500">
                        {draftActiveCount} filter{draftActiveCount === 1 ? "" : "s"} selected
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {draftActiveCount > 0 && (
                      <button
                        type="button"
                        onClick={handleClearAllDrafts}
                        className="text-xs font-semibold text-red-600 hover:text-red-700 underline"
                      >
                        Clear all
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setDrawerOpen(false)}
                      className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition"
                    >
                      <FaTimes className="text-sm" />
                    </button>
                  </div>
                </div>

                {/* Drawer Body - Scrollable Categories */}
                <div className="flex-1 overflow-y-auto overscroll-contain p-6 space-y-6">
                  {Object.entries(filterOptions).map(([filterType, options]) => {
                    const selectedValues = Array.isArray(draftFilters[filterType])
                      ? draftFilters[filterType]
                      : draftFilters[filterType] && draftFilters[filterType] !== "All"
                      ? [draftFilters[filterType]]
                      : [];

                    return (
                      <div key={filterType} className="border-b border-gray-100 pb-5 last:border-b-0">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs font-bold uppercase tracking-wider text-gray-700">
                            {filterType}
                          </span>
                          {selectedValues.length > 0 && (
                            <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">
                              {selectedValues.length} selected
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-1">
                          {options.map((option) => {
                            const isSelected = selectedValues.includes(option);
                            return (
                              <button
                                key={option}
                                type="button"
                                onClick={() => handleDraftFilterClick(filterType, option)}
                                className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-xs font-medium transition ${
                                  isSelected
                                    ? "border-red-500 bg-red-50 text-red-700 font-semibold"
                                    : "border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100 hover:border-gray-300"
                                }`}
                              >
                                <span
                                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[9px] ${
                                    isSelected
                                      ? "border-red-600 bg-red-600 text-white"
                                      : "border-gray-300 bg-white text-transparent"
                                  }`}
                                >
                                  <FaCheck />
                                </span>
                                <span className="truncate">{formatFilterValue(filterType, option)}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Drawer Footer */}
                <div className="border-t border-gray-100 px-6 py-4 bg-gray-50 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setDrawerOpen(false)}
                    className="flex-1 rounded-xl border border-gray-300 bg-white py-2.5 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleApplyDraftFilters}
                    className="flex-1 rounded-xl bg-[#bc1535] py-2.5 text-xs font-semibold text-white shadow hover:bg-red-700 transition"
                  >
                    Apply Filters ({draftActiveCount})
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* Active Filters */}
          <div className="flex flex-wrap gap-2 mt-2">
            {Object.entries(selectedFilters).map(([type, values]) => {
              if (values == null || values === "" || values === "All") return null;
              if (Array.isArray(values) && values.length === 0) return null;

              const valuesArray = Array.isArray(values) ? values : [values];
              return valuesArray.map((value) => {
                if (value == null || value === "" || value === "All") return null;

                return (
                  <div
                    key={`${type}-${value}`}
                    className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                  >
                    <span>{formatFilterValue(type, value)}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveFilter(type, value)}
                      className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors"
                      aria-label={`Remove ${formatFilterValue(type, value)} filter`}
                    >
                      <FaTimes className="text-[9px]" />
                    </button>
                  </div>
                );
              });
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvertisingHeader;
